import { CircularProgress } from "@material-ui/core";
import Loader from "@module/loader";
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ConfirmPaymentData, StripeElements, StripeErrorType, StripePaymentElementOptions } from "@stripe/stripe-js";
import { APISERVICE } from "@util/apiService/RestClient";
import { useEffect, useState } from "react";

export default function CheckoutForm({ stripeModalDetails, isLoading, setIsLoading, setStripeModalDetails }: any) {

  const stripe = useStripe();
  const elements = useElements();

  const [message, setMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent!.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [stripe]);

  const closeStripeWindow = () => {
    setStripeModalDetails({ ...stripeModalDetails, options: { ...stripeModalDetails.options, clientSecret: "" } });
    APISERVICE.PUT(process.env.NEXT_PUBLIC_STRIPE_PAYMENT + "/cancel?paymentIntentId=" + stripeModalDetails.paymentIntentId, null)
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const isMembershipOrPackage = !!stripeModalDetails.callbackUrl;
    const returnUrl = isMembershipOrPackage
      ? stripeModalDetails.callbackUrl
      : `${stripeModalDetails.redirectUrl}?status=paid&id=${stripeModalDetails.order.id}`;
    let options: { elements: StripeElements, confirmParams: ConfirmPaymentData } = {
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            "address": {
              "city": stripeModalDetails.address.city,
              "country": stripeModalDetails.address.country,//need to fetch this
              "line1": stripeModalDetails.address.line,
              "line2": stripeModalDetails.address.area,
              "postal_code": stripeModalDetails.address.code,
              "state": stripeModalDetails.address.landmark,//need to add state options in user registration
            },
            "email": stripeModalDetails.user.email || "",
            "name": stripeModalDetails.user.firstName,
            "phone": stripeModalDetails.user.mobileNo
          }
        },
        payment_method: "pm_card_visa"
      },
    }

    const stripeResponse = await stripe.confirmPayment(options);
    const { error } = stripeResponse

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    let f: StripeErrorType
    if (error.type === "card_error" || error.type === "validation_error" || "payment_intent_authentication_failure") {
      setMessage(error.message);
    } else {
      setMessage("An unexpected error occurred.");
    }

    setIsLoading(false);
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: "tabs",
    wallets: {
      googlePay: "auto",
      applePay: "auto",
    }
  }

  return (
    <form className="stripePaymentForm" onSubmit={handleSubmit}>
      <div className="stripe-modal-body">
        <PaymentElement options={paymentElementOptions} />
        {message && <div className="error">{message}</div>}
      </div>
      <div className="stripe-modal-footer">
        <button
          type="button"
          disabled={isLoading || !stripe || !elements}
          onClick={closeStripeWindow}
          className="primary-btn border-btn stripeConfirmBtn"
        >
          {isLoading ? <CircularProgress size={21} /> : "Cancel"}
        </button>
        <button
          type="submit"
          disabled={isLoading || !stripe || !elements}
          className="primary-btn stripeConfirmBtn"
        >
          {isLoading ? <CircularProgress size={21} /> : "Proceed"}
        </button>
      </div>
      {isLoading && <Loader />}
    </form>
  );
}